import { Link } from "react-router-dom";
import { Mail, MessageCircle, Phone, ShieldCheck, FileText, RefreshCcw, HelpCircle, Send, Facebook } from "lucide-react";

function PageShell({ title, subtitle, icon: Icon, children, testid }) {
  return (
    <div data-testid={testid} className="max-w-4xl mx-auto px-4 md:px-8 py-12">
      <div className="mb-10 pb-6 border-b border-white/10">
        <div className="flex items-center gap-3 mb-3">
          {Icon && (
            <div className="w-10 h-10 bg-[#2563EB]/15 border border-[#2563EB]/40 flex items-center justify-center">
              <Icon className="w-5 h-5 text-[#2563EB]" />
            </div>
          )}
          <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">// USTORE · LEGAL</span>
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl text-white">{title}</h1>
        {subtitle && <p className="mt-3 text-white/60 text-base max-w-2xl">{subtitle}</p>}
      </div>
      <div className="prose prose-invert max-w-none">{children}</div>
    </div>
  );
}

function H2({ children }) {
  return <h2 className="font-display font-black text-xl text-white mt-8 mb-3 uppercase tracking-wider">{children}</h2>;
}
function P({ children }) {
  return <p className="text-white/70 text-sm leading-relaxed mb-3">{children}</p>;
}
function UL({ children }) {
  return <ul className="list-disc pl-5 space-y-2 text-white/70 text-sm leading-relaxed mb-4">{children}</ul>;
}

// ─────────────────────────────────────────────────────────────
export function HelpCenter() {
  const faqs = [
    { q: "Bagaimana cara top-up saldo?", a: "Buka menu Wallet → klik Top-up → pilih channel pembayaran (BCA VA, OVO, DANA, ShopeePay, dll) → ikuti instruksi pembayaran. Saldo otomatis masuk setelah pembayaran berhasil." },
    { q: "Bagaimana cara ikut auction?", a: "Buka menu Auction → pilih item yang diminati → pastikan saldo cukup → klik Place Bid. Saldo akan dikunci sebesar bid kamu. Kalau outbid, saldo otomatis dikembalikan." },
    { q: "Apa itu BIN (Buy It Now)?", a: "BIN adalah harga instant-buy. Kalau seller set BIN, kamu bisa langsung beli auction tanpa nunggu countdown. Klik tombol 'Buy Now Rp X' di halaman auction." },
    { q: "Berapa lama garansi akun?", a: "Garansi berbeda per kategori: Akun 7 hari, Top-up via ID 1 hari, Top-up via Login 7 hari, Voucher 6 jam. Kalau masalah ditemukan dalam masa garansi, lapor via Pengaduan." },
    { q: "Bagaimana cara komplain transaksi?", a: "Buka halaman order → klik Lapor Masalah. Tim admin akan mediasi antara kamu dan seller. Saldo seller masih dikunci sampai komplain selesai." },
    { q: "Channel pembayaran apa saja yang didukung?", a: "BCA, BNI, BRI, Mandiri Virtual Account, OVO, DANA, ShopeePay, QRIS, dan Alfamart/Indomaret. Semua via Tripay." },
  ];
  return (
    <PageShell title="Help Center" subtitle="Pertanyaan yang sering ditanyakan seputar marketplace, auction, dan transaksi Ustore." icon={HelpCircle} testid="page-help">
      {faqs.map((f, i) => (
        <details key={i} className="border border-white/10 hover:border-[#2563EB]/40 mb-3 transition-colors group" data-testid={`faq-${i}`}>
          <summary className="cursor-pointer px-5 py-4 font-bold text-white text-sm flex items-center justify-between">
            {f.q}
            <span className="text-[#2563EB] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
          </summary>
          <p className="px-5 pb-4 text-white/70 text-sm leading-relaxed border-t border-white/10 pt-3">{f.a}</p>
        </details>
      ))}
      <div className="mt-10 p-6 bg-[#2563EB]/10 border border-[#2563EB]/40">
        <p className="text-sm text-white">Tidak menemukan jawaban?</p>
        <Link to="/contact" className="inline-flex items-center gap-2 mt-3 px-5 py-2.5 bg-[#2563EB] hover:bg-[#1d4ed8] text-white text-xs uppercase tracking-widest font-bold" data-testid="help-to-contact">
          <Send className="w-4 h-4" /> Hubungi Kami
        </Link>
      </div>
    </PageShell>
  );
}

// ─────────────────────────────────────────────────────────────
export function RefundPolicy() {
  return (
    <PageShell title="Refund Policy" subtitle="Kebijakan pengembalian dana Ustore — berlaku untuk semua transaksi marketplace dan auction." icon={RefreshCcw} testid="page-refund">
      <H2>1. Ketentuan Umum</H2>
      <P>Refund akan diproses ke saldo (wallet) buyer dalam waktu maksimal 24 jam setelah klaim disetujui. Refund ke rekening bank butuh 1–3 hari kerja tergantung bank.</P>

      <H2>2. Kondisi yang Memenuhi Syarat Refund</H2>
      <UL>
        <li>Akun yang dibeli tidak sesuai deskripsi atau tidak bisa login</li>
        <li>Top-up gagal masuk ke akun game dalam 24 jam</li>
        <li>Voucher tidak valid atau sudah expired saat diterima</li>
        <li>Seller tidak respon dalam 48 jam setelah order dikonfirmasi</li>
        <li>Akun di-recall oleh original owner dalam masa garansi</li>
      </UL>

      <H2>3. Kondisi yang TIDAK Memenuhi Syarat</H2>
      <UL>
        <li>Sudah lewat masa garansi (cek detail per kategori)</li>
        <li>Akun di-banned karena pelanggaran buyer setelah pembelian</li>
        <li>Bukti tidak cukup atau tidak ada bukti chat dengan seller</li>
        <li>Buyer mengubah informasi akun (email, password recovery) sebelum komplain</li>
      </UL>

      <H2>4. Cara Mengajukan Refund</H2>
      <P>1. Buka halaman order yang bermasalah<br />2. Klik "Lapor Masalah" → isi alasan + upload bukti screenshot<br />3. Tim admin akan mediasi dalam 1×24 jam<br />4. Setelah disetujui, saldo refund otomatis masuk ke wallet</P>

      <H2>5. Garansi per Kategori</H2>
      <UL>
        <li><b>Akun Game:</b> 7 hari sejak pembelian</li>
        <li><b>Top-up via ID:</b> 1 hari sejak diproses</li>
        <li><b>Top-up via Login:</b> 7 hari sejak diproses</li>
        <li><b>Voucher Digital:</b> 6 jam sejak dikirim</li>
        <li><b>Joki Service:</b> 7 hari sejak service selesai</li>
      </UL>
    </PageShell>
  );
}

// ─────────────────────────────────────────────────────────────
export function ContactUs() {
  return (
    <PageShell title="Contact Us" subtitle="Tim Ustore siap bantu kamu 7 hari seminggu. Hubungi via channel di bawah." icon={Mail} testid="page-contact">
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <a href="https://wa.me/6283830173704" target="_blank" rel="noopener noreferrer" className="border border-white/10 hover:border-[#25D366] p-5 flex items-center gap-4 transition-colors" data-testid="contact-whatsapp">
          <MessageCircle className="w-6 h-6 text-[#25D366]" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40">WhatsApp</div>
            <div className="font-bold text-white text-sm">083830173704</div>
          </div>
        </a>
        <a href="mailto:Topup20k21@gmail.com" className="border border-white/10 hover:border-[#2563EB] p-5 flex items-center gap-4 transition-colors" data-testid="contact-email">
          <Mail className="w-6 h-6 text-[#2563EB]" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40">Email</div>
            <div className="font-bold text-white text-sm">Topup20k21@gmail.com</div>
          </div>
        </a>
        <a href="https://www.facebook.com/reza.alga129" target="_blank" rel="noopener noreferrer" className="border border-white/10 hover:border-[#1877F2] p-5 flex items-center gap-4 transition-colors md:col-span-2" data-testid="contact-facebook">
          <Facebook className="w-6 h-6 text-[#1877F2]" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40">Facebook</div>
            <div className="font-bold text-white text-sm">facebook.com/reza.alga129</div>
          </div>
        </a>
      </div>

      <H2>Response Time</H2>
      <UL>
        <li>Komplain transaksi: maks. 1×24 jam</li>
        <li>Refund disetujui → masuk wallet: maks. 24 jam</li>
      </UL>
    </PageShell>
  );
}

// ─────────────────────────────────────────────────────────────
export function TermsOfService() {
  return (
    <PageShell title="Terms of Service" subtitle="Syarat & Ketentuan penggunaan platform Ustore. Berlaku efektif sejak Juni 2026." icon={FileText} testid="page-terms">
      <H2>1. Penerimaan Syarat</H2>
      <P>Dengan menggunakan platform Ustore (selanjutnya disebut "Platform"), kamu setuju terikat oleh Syarat & Ketentuan ini. Jika tidak setuju, mohon tidak menggunakan layanan kami.</P>

      <H2>2. Akun Pengguna</H2>
      <UL>
        <li>Pengguna wajib berusia minimal 17 tahun atau memiliki persetujuan wali</li>
        <li>Informasi akun harus akurat dan dijaga kerahasiaannya</li>
        <li>1 user hanya boleh memiliki 1 akun (buyer & seller boleh digabung)</li>
        <li>Akun yang terdeteksi melakukan fraud akan di-suspend permanen</li>
      </UL>

      <H2>3. Transaksi & Pembayaran</H2>
      <UL>
        <li>Semua transaksi dalam Rupiah (IDR) via gateway resmi Tripay</li>
        <li>Saldo wallet adalah escrow — bukan rekening bank, tidak berbunga</li>
        <li>Saldo bisa di-withdraw seller setelah masa garansi & komplain berakhir</li>
        <li>Biaya admin & fee seller mengikuti konfigurasi terkini di halaman fee</li>
      </UL>

      <H2>4. Auction</H2>
      <UL>
        <li>Bid bersifat MENGIKAT — saldo otomatis dikunci saat bid placed</li>
        <li>Auction time-window aktif 08:00 – 21:00 WIB (di luar jam, status frozen)</li>
        <li>Anti-snipe: bid 2 menit terakhir auto-extend countdown 2 menit</li>
        <li>BIN (Buy It Now) langsung close auction dan saldo di-settle</li>
        <li>Hanya game whitelist dari admin yang boleh diauction</li>
      </UL>

      <H2>5. Kewajiban Seller</H2>
      <UL>
        <li>Memberikan akses produk dalam waktu maksimal 24 jam setelah order</li>
        <li>Memberikan garansi sesuai kategori (akun 7 hari, voucher 6 jam, dst)</li>
        <li>Tidak melakukan recall akun selama masa garansi</li>
        <li>Bersedia mediasi via admin jika ada komplain</li>
      </UL>

      <H2>6. Larangan</H2>
      <UL>
        <li>Menjual akun hasil hack, phising, atau curian</li>
        <li>Melakukan jual-beli di luar platform (off-platform) — saldo dilock permanen</li>
        <li>Spam, scam, atau menipu user lain</li>
        <li>Menggunakan bot/automation untuk bid auction</li>
      </UL>

      <H2>7. Pemutusan</H2>
      <P>Ustore berhak suspend atau hapus akun yang melanggar T&amp;C tanpa pemberitahuan. Saldo yang tersisa akan di-review case-by-case.</P>

      <H2>8. Perubahan Syarat</H2>
      <P>Ustore dapat mengubah T&amp;C sewaktu-waktu. Perubahan signifikan akan dinotifikasi via email atau banner di platform.</P>

      <H2>9. Hukum yang Berlaku</H2>
      <P>T&amp;C ini tunduk pada hukum Republik Indonesia. Sengketa diselesaikan via Pengadilan Negeri Jakarta Pusat.</P>
    </PageShell>
  );
}

// ─────────────────────────────────────────────────────────────
export function PrivacyPolicy() {
  return (
    <PageShell title="Privacy Policy" subtitle="Bagaimana Ustore mengumpulkan, menggunakan, dan melindungi data pribadi kamu." icon={ShieldCheck} testid="page-privacy">
      <H2>1. Data yang Dikumpulkan</H2>
      <UL>
        <li><b>Akun:</b> nama, email, password (di-hash dengan bcrypt)</li>
        <li><b>Transaksi:</b> riwayat order, saldo, bid auction, detail pembayaran</li>
        <li><b>Komunikasi:</b> chat dengan seller, log komplain</li>
        <li><b>Teknis:</b> IP address, browser, device, log aktivitas (untuk keamanan)</li>
      </UL>

      <H2>2. Penggunaan Data</H2>
      <UL>
        <li>Memproses transaksi & mengirim notifikasi (email via Resend)</li>
        <li>Verifikasi identitas & deteksi fraud</li>
        <li>Mediasi komplain antar buyer-seller</li>
        <li>Analitik internal untuk improvement platform</li>
      </UL>

      <H2>3. Berbagi Data dengan Pihak Ketiga</H2>
      <P>Kami HANYA membagikan data dengan:</P>
      <UL>
        <li><b>Tripay</b> — payment gateway untuk processing transaksi</li>
        <li><b>Resend</b> — email notification service</li>
        <li><b>Penegak hukum</b> — jika diwajibkan oleh hukum yang berlaku</li>
      </UL>
      <P>Kami TIDAK menjual data ke advertiser atau pihak ketiga lainnya.</P>

      <H2>4. Penyimpanan & Keamanan</H2>
      <UL>
        <li>Password di-hash menggunakan bcrypt (tidak bisa di-decrypt)</li>
        <li>Komunikasi via HTTPS/TLS</li>
        <li>Session via httpOnly cookie</li>
        <li>Database backup harian dengan enkripsi</li>
      </UL>

      <H2>5. Hak Pengguna</H2>
      <UL>
        <li>Akses & request copy data pribadi</li>
        <li>Update/koreksi data via halaman Account</li>
        <li>Request penghapusan akun (kecuali ada transaksi pending)</li>
        <li>Opt-out dari email marketing kapan saja</li>
      </UL>

      <H2>6. Cookies</H2>
      <P>Kami menggunakan cookie esensial untuk:</P>
      <UL>
        <li>Authentication (httpOnly session cookie)</li>
        <li>Cart & wishlist (localStorage)</li>
      </UL>
      <P>Tidak ada tracking cookie pihak ketiga (Google Analytics, FB Pixel, dll).</P>

      <H2>7. Anak di Bawah Umur</H2>
      <P>Platform tidak ditujukan untuk anak di bawah 17 tahun. Jika kami mengetahui data anak di bawah umur terkumpul, akan segera dihapus.</P>

      <H2>8. Kontak</H2>
      <P>Pertanyaan terkait privasi: <a href="mailto:privacy@ubidstore.com" className="text-[#2563EB] underline">privacy@ubidstore.com</a></P>
    </PageShell>
  );
}
